import { api } from './api'
import type { User, TeacherProfile, TeacherApplication } from '../types/user'
import type { Booking, BookingWithDetails, TimeSlot } from '../types/booking'
import type { Material, Course } from '../types/material'
import type { Earning, EarningSummary } from '../types/earning'

// 认证相关API
export const authApi = {
  // 登录
  login: async (email: string, password: string) => {
    return api.post<{ user: User; token: string }>('/auth/login', {
      email,
      password,
      user_type: 'teacher'
    })
  },

  // 手机号登录
  loginWithPhone: async (phone: string, password: string) => {
    return api.post<{ user: User; token: string }>('/auth/login', {
      phone,
      password,
      user_type: 'teacher'
    })
  },

  // 注册
  register: async (userData: any) => {
    return api.post('/auth/register', {
      ...userData,
      user_type: 'teacher'
    })
  },

  // 登出
  logout: async () => {
    return api.post('/auth/logout')
  },

  // 刷新token
  refreshToken: async () => {
    return api.post<{ token: string }>('/auth/refresh')
  }
}

// 教师资料相关API
export const profileApi = {
  // 获取教师资料
  getProfile: async () => {
    return api.get<TeacherProfile>('/users/profile')
  },

  // 更新教师资料
  updateProfile: async (profileData: Partial<TeacherProfile>) => {
    return api.put<TeacherProfile>('/users/profile', profileData)
  },

  // 更新基本信息
  updateBasicInfo: async (data: Partial<User>) => {
    return api.put<User>('/users/profile', data)
  },

  // 上传头像
  uploadAvatar: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'profile-photo')

    const uploadResp = await api.request<{ success: boolean; data?: { url: string } }>(
      '/upload',
      { method: 'POST', body: formData, headers: {} }
    )

    if (!uploadResp.success || !uploadResp.data?.url) {
      throw new Error('头像上传失败')
    }

    const updateResp = await api.put('/users/profile', { avatar_url: uploadResp.data.url })
    if (!updateResp.success) {
      throw new Error(updateResp.error?.message || '更新头像失败')
    }

    return { success: true, data: { avatar_url: uploadResp.data.url } }
  }
}

// 教师注册流程API
export const registerApi = {
  uploadIntroVideo: async (file: File) => {
    const formData = new FormData()
    formData.append('video', file)
    
    // 获取临时token（注册流程中）或JWT token（已登录）
    let headers: Record<string, string> = {}
    const tempToken = localStorage.getItem('talknect-teacher-temp-token')
    const authRaw = localStorage.getItem('talknect-teacher-auth')
    
    if (authRaw) {
      try {
        const obj = JSON.parse(authRaw)
        const token = obj?.token || obj?.state?.token
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      } catch (e) {
        // 如果没有JWT，尝试使用临时token
        if (tempToken) {
          headers['Authorization'] = `Bearer ${tempToken}`
        }
      }
    } else if (tempToken) {
      // 使用临时token
      headers['Authorization'] = `Bearer ${tempToken}`
    }
    
    return api.request<{ success: boolean; data?: { video_url: string } }>(
      '/teachers/register/upload-video',
      { method: 'POST', body: formData, headers }
    )
  },

  submitForReview: async (email?: string) => {
    if (email) {
      return api.post('/teachers/register/submit-for-review', { email })
    }
    return api.post('/teachers/register/submit-for-review')
  }
  ,
  getRegisterStatus: async () => {
    return api.get('/teachers/register/status')
  }
}

// 课程相关API
export const bookingApi = {
  // 获取课程列表
  getBookings: async (params?: {
    status?: string
    date_from?: string
    date_to?: string
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    return api.get<BookingWithDetails[]>(`/teacher/bookings${query ? `?${query}` : ''}`)
  },

  // 获取单个课程详情
  getBooking: async (id: number) => {
    return api.get<BookingWithDetails>(`/teacher/bookings/${id}`)
  },

  // 确认课程
  confirmBooking: async (id: number) => {
    return api.patch(`/teacher/bookings/${id}/confirm`)
  },

  // 取消课程
  cancelBooking: async (id: number, reason: string) => {
    return api.patch(`/teacher/bookings/${id}/cancel`, { reason })
  },

  // 完成课程
  completeBooking: async (id: number, notes?: string) => {
    return api.patch(`/teacher/bookings/${id}/complete`, { notes })
  },

  // 开始课程（生成会议链接）
  startBooking: async (id: number) => {
    return api.post(`/teacher/bookings/${id}/start`)
  }
}

// 时间安排相关API
export const scheduleApi = {
  // 获取时间槽
  getTimeSlots: async () => {
    return api.get<TimeSlot[]>('/teacher/schedule/time-slots')
  },

  // 更新时间槽
  updateTimeSlots: async (timeSlots: Partial<TimeSlot>[]) => {
    return api.put('/teacher/schedule/time-slots', { time_slots: timeSlots })
  },

  // 批量创建时间槽
  createTimeSlots: async (timeSlots: Omit<TimeSlot, 'id' | 'teacher_id' | 'created_at' | 'updated_at'>[]) => {
    return api.post('/teacher/schedule/time-slots', { time_slots: timeSlots })
  },

  // 获取可用时间段
  getAvailableSlots: async (date: string) => {
    return api.get<string[]>(`/teacher/schedule/available-slots?date=${date}`)
  }
}

// 学生管理API
export const studentApi = {
  // 获取学生列表
  getStudents: async (params?: {
    search?: string
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    return api.get(`/teacher/students${query ? `?${query}` : ''}`)
  },

  // 获取学生详情
  getStudent: async (id: number) => {
    return api.get(`/teacher/students/${id}`)
  }
}

// 教学材料API
export const materialApi = {
  // 获取材料列表（使用管理端公开接口）
  getMaterials: async (params?: {
    type?: string
    category?: string
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    // 默认只获取启用的教材
    queryParams.append('is_active', '1')
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    // 使用管理端教材接口（已开放为公开接口）
    return api.get<Material[]>(`/admin/materials${query ? `?${query}` : ''}`)
  },

  // 上传材料
  uploadMaterial: async (file: File, materialData: {
    title: string
    description?: string
    type: string
    category?: string
    tags?: string[]
    is_public: boolean
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', materialData.title)
    if (materialData.description) formData.append('description', materialData.description)
    formData.append('type', materialData.type)
    if (materialData.category) formData.append('category', materialData.category)
    if (materialData.tags) formData.append('tags', JSON.stringify(materialData.tags))
    formData.append('is_public', materialData.is_public.toString())

    return api.post<Material>('/teacher/materials', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // 删除材料
  deleteMaterial: async (id: number) => {
    return api.delete(`/teacher/materials/${id}`)
  },

  // 更新材料
  updateMaterial: async (id: number, data: Partial<Material>) => {
    return api.put(`/teacher/materials/${id}`, data)
  }
}

// 收益相关API
export const earningApi = {
  // 获取收益记录
  getEarnings: async (params?: {
    type?: string
    status?: string
    date_from?: string
    date_to?: string
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    return api.get<Earning[]>(`/teacher/earnings${query ? `?${query}` : ''}`)
  },

  // 获取收益统计
  getEarningSummary: async () => {
    return api.get<EarningSummary>('/teacher/earnings/summary')
  },

  // 申请提现
  requestWithdrawal: async (amount: number, method: string, account_info: any) => {
    return api.post('/teacher/earnings/withdrawal', {
      amount,
      method,
      account_info
    })
  }
}

// 课程管理API
export const courseApi = {
  // 获取课程列表
  getCourses: async (params?: {
    status?: string
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    return api.get<Course[]>(`/teacher/courses${query ? `?${query}` : ''}`)
  },

  // 创建课程
  createCourse: async (courseData: Omit<Course, 'id' | 'teacher_id' | 'created_at' | 'updated_at'>) => {
    return api.post<Course>('/teacher/courses', courseData)
  },

  // 更新课程
  updateCourse: async (id: number, courseData: Partial<Course>) => {
    return api.put(`/teacher/courses/${id}`, courseData)
  },

  // 删除课程
  deleteCourse: async (id: number) => {
    return api.delete(`/teacher/courses/${id}`)
  }
}

// 通知API
export const notificationApi = {
  // 获取通知列表
  getNotifications: async (params?: {
    unread_only?: boolean
    page?: number
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }

    const query = queryParams.toString()
    return api.get(`/teacher/notifications${query ? `?${query}` : ''}`)
  },

  // 标记为已读
  markAsRead: async (id: number) => {
    return api.patch(`/teacher/notifications/${id}/read`)
  },

  // 标记全部为已读
  markAllAsRead: async () => {
    return api.patch('/teacher/notifications/read-all')
  }
}
